import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "translation/useTranslation";
import { fetchSuggestedActions, getSuggestedActions } from "store/suggestedActionsSlice";
import { fetchSavedFilters, getSavedFilters } from "store/savedFiltersSlice";
import AssignPeriodAction from "./AssignPeriodAction";
import CreateBalancedScorecardAction from "./CreateBalancedScorecardAction";
import FillFieldsAction from "./FillFieldsAction";
import EmployeeFilterBox from "./EmployeeFilterBox";
import Link from "components/ui/Link";
import ActivateEmployeesAction from "./ActivateEmployeesAction";
import UpdateScorecardAction from "./UpdateScorecardAction";
import { periodActivityTranslationFallback } from "helpers/periodActivityTranslations";

const actionTypeBodies = {
  assign_to_period: AssignPeriodAction,
  activate_employees: ActivateEmployeesAction,
  create_balanced_scorecard: CreateBalancedScorecardAction,
  create_evaluation: CreateBalancedScorecardAction,
  update_balanced_scorecard: UpdateScorecardAction,
};

const twoStepActionTypes = ['assign_to_period', 'activate_employees', 'update_balanced_scorecard'];

const selfContainedActionTypes = ['assign_to_period', 'update_balanced_scorecard'];

export default function ActionDetail({ variant = 'suggested' }) {
  const { hashId } = useParams();
  const dispatch = useDispatch();
  const { editableTranslate } = useTranslation();

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const suggestedActions = useSelector(getSuggestedActions);
  const savedFilters = useSelector(getSavedFilters);

  const [page, setPage] = useState(1);
  const [filterCount, setFilterCount] = useState(null);

  // Custom-filter variant: the ad-hoc filter applied from the filters-box, and
  // the raw rows kept so returning to the box preserves the entered conditions.
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [filterRows, setFilterRows] = useState(null);

  const handleCustomApply = (query, rows) => {
    setFilterRows(rows);
    setPage(1);
    setAppliedFilters(query);
  };

  const handleChangeFilter = () => {
    setPage(1);
    setAppliedFilters(null);
  };

  useEffect(() => {
    dispatch(fetchSuggestedActions());
    dispatch(fetchSavedFilters());
  }, [dispatch]);

  const action = useMemo(() => suggestedActions?.find(a => a.hash_id === hashId) || null, [suggestedActions, hashId]);

  const savedFilter = useMemo(() => savedFilters?.find(f => f.hash_id === hashId) || null, [savedFilters, hashId]);

  // Budget-validation actions carry their own filters/fields and are not backed
  // by a SavedFilter, so they resolve from the suggested-action item directly.
  const isValidationAction = useMemo(() => action?.source === 'budget_validation', [action]);

  const isSelfContainedAction = useMemo(
    () => isValidationAction || selfContainedActionTypes.includes(action?.action_type),
    [isValidationAction, action]
  );

  const effectiveActionType = action?.action_type || savedFilter?.action_type || null;

  const steps = useMemo(() => {
    if (twoStepActionTypes.includes(effectiveActionType)) {
      return [
        { n: 1, label: editableTranslate('action.detail.step.choose', 'Auswahl') },
        { n: 2, label: editableTranslate('action.detail.step.apply', 'Anwenden') },
      ];
    }
    return [
      { n: 1, label: editableTranslate('action.detail.step.choose', 'Auswahl') },
      { n: 2, label: editableTranslate('action.detail.step.precheck', 'Prüfung') },
      { n: 3, label: editableTranslate('action.detail.step.apply', 'Anwenden') },
    ];
  }, [effectiveActionType, editableTranslate]);

  const isLoading = suggestedActions === null || savedFilters === null;

  useEffect(() => {
    // Validation actions have no SavedFilter by design, so don't treat their
    // missing SavedFilter as "not found".
    if (savedFilters && !savedFilter && suggestedActions && !isSelfContainedAction && ['suggested', 'system-filter'].includes(variant)) {
      navigate('/settings/actions');
    }
  }, [savedFilter, savedFilters, variant, suggestedActions, isSelfContainedAction, navigate]);

  const renderSubtitle = () => {
    if (variant === 'system-filter' && savedFilter) {
      return <p className="section-description mt-2 mb-0">
        <span>
          <span className="font-weight-bold">{filterCount}</span> {editableTranslate(savedFilter.translation_key, periodActivityTranslationFallback(savedFilter.translation_key, savedFilter.name))}
        </span>
      </p>;
    }

    if (variant === 'suggested' && action) {
      return <p className="section-description mt-2 mb-0">
        <span>
          <span className="font-weight-bold">{action.count}</span> {editableTranslate((action?.budget_setting ? 'calculation_overview.fix_' : '') + action.translation_key, periodActivityTranslationFallback(action.translation_key, action.label))}
        </span>
      </p>;
    }

    if (variant === 'custom') {
      if (appliedFilters) {
        return <p className="section-description mt-2 mb-0">
          <span>
            <span className="font-weight-bold">{filterCount}</span> {editableTranslate('action.custom.filtered_employees', 'Gefilterte Beschäftigte')}
          </span>
        </p>;
      } else {
        return <p className="section-description mt-2 mb-0">
          {editableTranslate('action.custom.description', 'Konfigurieren Sie Filter oder wählen Sie einen gespeicherten Filter.')} <Link data-testid="manage-filters-link" to="/settings/filters" className="red-dark-link">{editableTranslate('action.custom.manage_filters', 'Filter verwalten')}</Link>.
        </p>;
      }
    }

    return null;
  };

  const renderContent = () => {
    if (isLoading) {
      return <div className="text-center p-5" data-testid="loading">
        <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
      </div>;
    }

    if (variant === 'system-filter') {
      if (!savedFilter) {
        return <div className="mt-3 text-muted">
          {editableTranslate('action.detail.not_found', 'Aktion nicht gefunden.')}
        </div>;
      }

      const SystemFilterAction = actionTypeBodies[savedFilter.action_type];
      if (SystemFilterAction) {
        return <SystemFilterAction action={savedFilter} filters={savedFilter.filters} page={page} setPage={setPage} filterCount={filterCount} setFilterCount={setFilterCount} />;
      }

      return <FillFieldsAction savedFilter={savedFilter} filters={savedFilter.filters} page={page} setPage={setPage} filterCount={filterCount} setFilterCount={setFilterCount} />;
    }

    if (variant === 'custom') {
      if (!appliedFilters) {
        return <div className="mt-3">
          <EmployeeFilterBox onApply={handleCustomApply} defaultRows={filterRows} />
        </div>;
      }

      return <FillFieldsAction filters={appliedFilters} page={page} setPage={setPage} filterCount={filterCount} setFilterCount={setFilterCount} onBack={handleChangeFilter} />;
    }

    // Validation-driven action: reuse the fill-fields flow, restricted to the
    // fields the validation asks for and with only the update_data tab.
    if (isValidationAction && action) {
      return <FillFieldsAction
        filters={action.filters}
        allowedFieldKeys={action.action_fields}
        restrictToUpdateData={true}
        page={page}
        setPage={setPage}
        filterCount={filterCount}
        setFilterCount={setFilterCount}
        backTo={searchParams.get('source') === 'calculation' ? `/calculation/preliminary-check?hash_id=` + action?.budget_setting?.hash_id : null}
      />;
    }

    if (!action || (!savedFilter && !isSelfContainedAction)) {
      return <div className="mt-3 text-muted">
        {editableTranslate('action.detail.not_found', 'Aktion nicht gefunden.')}
      </div>;
    }

    const Body = actionTypeBodies[action.action_type];

    if (!Body) {
      return <div className="mt-3 text-muted">
        {editableTranslate('action.detail.unsupported', 'Dieser Aktionstyp wird noch nicht unterstützt.')}
      </div>;
    }

    return <Body action={action} filters={savedFilter?.filters ?? action.filters} page={page} setPage={setPage} filterCount={filterCount} setFilterCount={setFilterCount} />;
  };

  const headerTitle = useMemo(() => {
    if (variant === 'suggested') return editableTranslate('action.suggested.title', 'Vorgeschlagene Aktionen');
    else if (variant === 'system-filter') return editableTranslate('action.system_filters.title', 'Systemfilter');
    else return editableTranslate('action.custom.title', 'Benutzerdefinierte Filter');
  }, [variant, editableTranslate]);

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className="bg-white w-100 p-4 content-card">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">{headerTitle}</h4>
              {renderSubtitle()}
            </div>
            <div>
              {((variant === 'custom' && appliedFilters && filterCount > 0) || (variant === 'suggested' && action?.count > 0) || (variant === 'system-filter' && filterCount > 0)) && <div className="action-stepper mb-4">
                {steps.map((s, i) => (
                  <Fragment key={s.n}>
                    <div className={`action-stepper-step ${page === s.n ? 'active' : ''} ${page > s.n ? 'completed' : ''}`}>
                      <span className="action-stepper-circle">
                        {page > s.n ? <i className="bi bi-check-lg"></i> : s.n}
                      </span>
                      <span className="action-stepper-label">{s.label}</span>
                    </div>
                    {i < steps.length - 1 && <span className={`action-stepper-line ${page > s.n ? 'filled' : ''}`} />}
                  </Fragment>
                ))}
              </div>}
            </div>
          </div>

          {renderContent()}
        </div>
      </div>
    </>
  );
}
